"use client";

import { AnalyticsScript } from "@analytics";
import { AnalyticsRouteListener } from "@analytics/components/AnalyticsRouteListener";
import { IdentityBootstrap } from "@analytics/components/IdentityBootstrap";
import { OutboundLinkAnalyticsListener } from "@analytics/components/OutboundLinkAnalyticsListener";
import { AnalyticsCaptureReadyProvider } from "@analytics/lib/analytics-capture-ready-context";
import { ProgressProvider } from "@bprogress/next/app";
import { Toaster } from "@repo/ui/components/sonner";
import { ApiClientProvider } from "@shared/components/ApiClientProvider";
import { ConsentAwareThemeProvider } from "@shared/components/ConsentAwareThemeProvider";
import { ConsentBanner } from "@shared/components/consent/ConsentBanner";
import { ConsentPreferencesDialog } from "@shared/components/consent/ConsentPreferencesDialog";
import { CursorPreferenceProvider } from "@shared/lib/cursor-preference-context";
import type { DehydratedState } from "@tanstack/react-query";
import { type PropsWithChildren, useCallback, useState } from "react";

interface ClientProvidersProps extends PropsWithChildren {
	dehydratedState?: DehydratedState;
}

/**
 * Identity bootstrap and capture gating sit **above** {@link ConsentAwareThemeProvider}
 * so toggling theme persistence (preferences consent) does not remount the identity
 * graph / PostHog bootstrap path or reset capture readiness.
 */
export function ClientProviders({
	children,
	dehydratedState,
}: ClientProvidersProps) {
	const [captureReady, setCaptureReady] = useState(false);

	const handleCaptureReady = useCallback(() => {
		setCaptureReady(true);
	}, []);

	const handleCaptureSuspended = useCallback(() => {
		setCaptureReady(false);
	}, []);

	return (
		<ApiClientProvider dehydratedState={dehydratedState}>
			<ProgressProvider
				height="4px"
				color="var(--color-primary)"
				options={{ showSpinner: false }}
				shallowRouting
				delay={250}
			>
				<AnalyticsCaptureReadyProvider ready={captureReady}>
					<IdentityBootstrap
						onCaptureReady={handleCaptureReady}
						onCaptureSuspended={handleCaptureSuspended}
					/>
					<ConsentBanner />
					<ConsentPreferencesDialog />
					{captureReady ? (
						<>
							<AnalyticsScript />
							<AnalyticsRouteListener />
							<OutboundLinkAnalyticsListener />
						</>
					) : null}
					<ConsentAwareThemeProvider>
						<CursorPreferenceProvider>
							{children}
						</CursorPreferenceProvider>
					</ConsentAwareThemeProvider>
					<Toaster position="top-right" />
				</AnalyticsCaptureReadyProvider>
			</ProgressProvider>
		</ApiClientProvider>
	);
}
